import { Box, TableCell, Table, CircularProgress } from "@mui/material";
import { createUseStyles } from "react-jss";
import RedoIcon from "@mui/icons-material/Redo";
import { useState } from "react";
import { theme } from "$utils/theme/bataillog";

import {
  ORDERS_FLEX_WIDTH,
  RECEPTIONS_FLEX_WIDTH,
} from "$containers/EDB/OrdersAndReceptions/constants";
import { Button, Text, Thead } from "$components";
import { TreeReference } from "$components/JReceptionTree/useReceptionForTree";
import { UpdateEDBProducts } from "$services/EDB.service";
import useAuth from "$hooks/useAuth";
import ReferenceType from "$types/ReferenceType";
import JTooltip from "$components/JTooltip/JTooltip";
import { EDB_IS_DISABLED_MSG } from "../constants";
import { ProductService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const { PRODUCT, ORDER } = ReferenceType;

type Props = {
  edb: any;
  emptySelectedItems: () => void;
  fetchEdb: () => void;
  fetchIntegratedOrders: (id: number | string, forceRefresh?: boolean) => void;
  isACommandIntegrated: boolean;
  isDisabled: boolean;
  selectedItems: TreeReference[];
  orders: any;
};

function Header({
  edb,
  emptySelectedItems,
  fetchEdb,
  fetchIntegratedOrders,
  isACommandIntegrated,
  isDisabled,
  selectedItems,
  orders,
}: Props) {
  const classes = useStyles();
  const { token } = useAuth();

  const [isSaving, setIsSaving] = useState(false);

  const handleOnAddToTrackingClick = async () => {
    setIsSaving(true);

    const getReferencesToFetch = (): string[] => {
      const refs: string[] = [];
      selectedItems.forEach((item) => {
        const rr = item.receptionReference;
        if (rr?.referenceType === PRODUCT) {
          const ref = rr.originalProductReference || rr.productReference;
          if (ref) refs.push(ref);
          return;
        }
        if (rr?.referenceType === ORDER) {
          // For ORDER references, the products are carried by `productQuantities`
          // where `productId` is actually a product reference.
          (item.productQuantities || []).forEach((pq) => {
            if (pq?.productId) refs.push(pq.productId);
          });
        }
      });
      return refs;
    };

    let data;
    data = await queryClient.getQueryData([
      CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
      {
        $inreference: getReferencesToFetch(),
      },
      selectedItems[0].reception.company,
    ]);
    if (!data) {
      data = await queryClient.fetchQuery(
        [
          CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
          {
            $inreference: getReferencesToFetch(),
          },
          selectedItems[0].reception.company,
        ],
        () =>
          ProductService.getProductByReference({
            $inreference: getReferencesToFetch(),
            company: selectedItems[0].reception.company,
          }),
        {
          staleTime: 5 * 1000 * 60,
        }
      );
    }

    const productList = data.flat();

    // Ugly fix: we don't have the quantity ordered in selectedItems (only the quantity received),
    // so we need to get it from orders
    const getProductQuantityFromOrder = (
      orderReference,
      suborderReference,
      productReference
    ) => {
      const product = orders
        .find((order) => order.reference === orderReference)
        ?.suborders.find((suborder) => suborder.reference === suborderReference)
        ?.suborderProducts.find(
          (product) => product.reference === productReference
        );

      return product ? product.quantity : 0;
    };

    const newProducts = selectedItems.reduce((res, item) => {
      let quantity;
      const {
        receptionReference,
        productQuantity,
        productQuantities,
        reception: { warehouse },
      } = item;
      const { productReference, originalProductReference, referenceType } =
        receptionReference;

      // A single receptionReference can (in some payloads) span multiple suborders for the same product reference.
      // We dedupe using a key that includes the suborder context when available so we don't lose a line.
      const selectionKey = `${receptionReference.id}-${
        productQuantity?.suborderProductId ??
        productQuantity?.suborderReference ??
        productQuantity?.productId ??
        "root"
      }`;
      const alreadyPresent = !!res.find(
        (v) => v.__selectionKey === selectionKey
      );
      if (alreadyPresent) return res;

      if (referenceType === PRODUCT) {
        const { orderReference, suborderReference, productId } =
          productQuantity || {};
        const product = productList.find(
          (p: any) =>
            p.reference === originalProductReference ||
            p.reference === productReference
        );
        const resolvedProductId =
          receptionReference?.productId || product?.id || product?._id;
        const resolvedProductReference =
          receptionReference?.originalProductReference ||
          receptionReference?.productReference;
        const quantityFromOrder = getProductQuantityFromOrder(
          orderReference,
          suborderReference,
          productId
        );
        // Rule: If more quantity is received than ordered then take the ordered quantity
        // else take the received quantity
        if (productQuantity.receivedQuantity >= quantityFromOrder) {
          quantity = quantityFromOrder;
        } else {
          quantity = productQuantity.receivedQuantity;
        }
        return [
          ...res,
          {
            _id: resolvedProductId,
            productReference: resolvedProductReference,
            warehouse,
            qty: quantity,
            receptionReferenceId: receptionReference.id,
            __selectionKey: selectionKey,
          },
        ];
      }
      if (referenceType === ORDER) {
        // ORDER references are selected at the "receptionReference" level, so `productQuantity` can be null.
        // We must expand the selection into its underlying productQuantities (often just one).
        // For "référencement à la réception", the quantity is the abstract one filled on the reception reference form,
        // so we must NOT rely on `receivedQuantity` (often 0/undefined depending on payload shape).
        let pqs = (productQuantities || []).filter((pq: any) => pq?.productId);
        if (pqs.length === 0 && productQuantity?.productId) {
          pqs = [productQuantity];
        }
        return [
          ...res,
          ...pqs.map((pq: any) => {
            const product = productList.find(
              (p: any) => p.reference === pq.productId
            );
            const resolvedProductId =
              receptionReference?.productId || product?.id || product?._id;
            const resolvedProductReference =
              receptionReference?.productReference;
            const q = receptionReference?.quantity ?? pq?.receivedQuantity ?? 1;
            quantity = Number.isFinite(Number(q)) ? Number(q) : 1;
            return {
              _id: resolvedProductId,
              productReference: resolvedProductReference,
              warehouse,
              qty: quantity,
              receptionReferenceId: receptionReference.id,
              __selectionKey: `${receptionReference.id}-${
                pq?.suborderReference ?? pq?.productId ?? "order"
              }`,
            };
          }),
        ];
      }
      return res;
    }, []);
    try {
      await UpdateEDBProducts(token, { newProducts }, edb._id);
      await fetchEdb();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setIsSaving(false);
      emptySelectedItems();
      await fetchIntegratedOrders(edb._id);
    }
  };

  const selectedItemsPlural = selectedItems.length > 1 ? "s" : "";

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Text type="h3" className={classes.subtitle}>
          Commande(s) intégrée(s)
        </Text>
        {isACommandIntegrated && (
          <Box display="flex">
            <Box className={classes.addToOpBlock}>
              {selectedItems.length > 0 && (
                <Text>
                  <b>{selectedItems.length}</b> sélectionné{selectedItemsPlural}
                </Text>
              )}
              <JTooltip title={isDisabled && EDB_IS_DISABLED_MSG}>
                <Button
                  startIcon={
                    <RedoIcon style={{ transform: "rotateZ(45deg)" }} />
                  }
                  disabled={
                    selectedItems.length === 0 || isSaving || isDisabled
                  }
                  onClick={handleOnAddToTrackingClick}
                >
                  {isSaving ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Ajouter au suivi"
                  )}
                </Button>
              </JTooltip>
            </Box>
          </Box>
        )}
      </Box>

      {isACommandIntegrated && (
        <Box display="flex" mb={2}>
          <Box flex={ORDERS_FLEX_WIDTH}>
            <Table>
              <Thead>
                {/* Align with the trees below */}
                <Box display="flex" justifyContent="space-between">
                  <TableCell>Commande restante</TableCell>
                  <span>
                    <TableCell
                      style={{
                        minWidth: "initial",
                        paddingRight: theme.spacing(1),
                        textAlign: "right",
                      }}
                    >
                      Qté commandée
                    </TableCell>
                    <TableCell
                      style={{
                        minWidth: "80px",
                        paddingLeft: 0,
                      }}
                    >
                      Unité
                    </TableCell>
                  </span>
                </Box>
              </Thead>
            </Table>
          </Box>
          <Box flex={RECEPTIONS_FLEX_WIDTH}>
            <Table>
              <Thead>
                {/* Align with the trees below */}
                <Box display="flex" justifyContent="space-between">
                  <TableCell>Réception</TableCell>
                  <span>
                    <TableCell
                      style={{
                        minWidth: "initial",
                        paddingRight: theme.spacing(1),
                        textAlign: "right",
                      }}
                    >
                      Qté reçue
                    </TableCell>
                    <TableCell
                      style={{
                        minWidth: "65px",
                        paddingLeft: 0,
                        paddingRight: 17,
                      }}
                    >
                      Unité
                    </TableCell>
                  </span>
                </Box>
              </Thead>
            </Table>
          </Box>
        </Box>
      )}
    </>
  );
}

export default Header;

const useStyles = createUseStyles(() => ({
  subtitle: {
    fontWeight: "bold",
  },
  addToOpBlock: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));
